import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './NoAccessPage.css';

const ICONS = {
  red: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  amber: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  neutral: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  illustrated: (
    <svg viewBox="0 0 200 140" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <ellipse cx="100" cy="118" rx="70" ry="6" fill="#E2E8F0" opacity="0.6" />
      <rect x="55" y="30" width="90" height="88" rx="4" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="2" />
      <rect x="63" y="38" width="74" height="72" rx="2" fill="#F8FAFC" stroke="#E2E8F0" strokeWidth="1.5" />
      <rect x="71" y="46" width="58" height="26" rx="2" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1" />
      <rect x="71" y="76" width="58" height="26" rx="2" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1" />
      <circle cx="124" cy="74" r="2.5" fill="#94A3B8" />
      <g transform="translate(118, 18)">
        <circle cx="20" cy="20" r="20" fill="#FEF2F2" />
        <circle cx="20" cy="20" r="20" fill="none" stroke="#FECACA" strokeWidth="1.5" />
        <rect x="13" y="19" width="14" height="11" rx="2" fill="none" stroke="#DC2626" strokeWidth="2" />
        <path d="M16 19v-3a4 4 0 0 1 8 0v3" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" />
      </g>
      <g transform="translate(28, 100) rotate(-15)" opacity="0.55">
        <circle cx="6" cy="6" r="5" fill="none" stroke="#94A3B8" strokeWidth="1.5" />
        <line x1="11" y1="6" x2="22" y2="6" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="18" y1="6" x2="18" y2="9" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="21" y1="6" x2="21" y2="10" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" />
      </g>
      <circle cx="42" cy="50" r="1.5" fill="#CBD5E1" />
      <circle cx="170" cy="68" r="1.5" fill="#CBD5E1" />
      <circle cx="178" cy="92" r="1.5" fill="#CBD5E1" />
    </svg>
  ),
};

const ModuleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z" />
  </svg>
);

const RoleIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21v-1a8 8 0 0 1 16 0v1" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const SpinnerIcon = () => (
  <svg className="kp-ad-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

/**
 * NoAccessPage / AccessDenied
 *
 * Visas när användaren saknar behörighet till en modul eller sektion.
 * Stödjer fyra toner (red/amber/neutral/illustrated) och två storlekar (full/compact).
 *
 * Props (alla optional):
 *  - tone: 'red' | 'amber' | 'neutral' | 'illustrated'   (default 'red')
 *  - compact: boolean                                     (default false)
 *  - title: string
 *  - description: string
 *  - module: string                — modulnamn (visas i info-kortet)
 *  - requiredRole: string          — roll som krävs (visas som badge)
 *  - onRequestAccess: () => void   — callback för "Begär åtkomst"
 *  - onBack: () => void            — om null används Link to "/"
 *  - adminContactHref: string      — om satt visas hint-länk
 *  - showBack: boolean             — default true (full), false (compact)
 */
export default function NoAccessPage({
  tone = 'red',
  compact = false,
  title,
  description,
  module: moduleName,
  requiredRole,
  onRequestAccess,
  onBack,
  adminContactHref,
  showBack,
}) {
  const finalTitle = title || 'Ej åtkomst';
  const finalDesc = description || (compact
    ? 'Du saknar behörighet till denna sektion.'
    : 'Du har inte behörighet att visa denna modul. Kontakta din systemadministratör för att få åtkomst.');

  const hasInfo = Boolean(moduleName || requiredRole);
  const renderBack = showBack !== undefined ? showBack : !compact;
  const safeTone = ['red', 'amber', 'neutral', 'illustrated'].includes(tone) ? tone : 'red';

  const [requestState, setRequestState] = useState('idle'); // 'idle' | 'submitting' | 'submitted'

  const handleRequestClick = async () => {
    if (!onRequestAccess || requestState !== 'idle') return;
    setRequestState('submitting');
    try {
      await onRequestAccess();
      setRequestState('submitted');
    } catch (err) {
      setRequestState('idle');
    }
  };

  const requestLabel =
    requestState === 'submitting' ? 'Skickar...' :
    requestState === 'submitted'  ? 'Begäran skickad' :
    'Begär åtkomst';

  return (
    <div className="kp-access-denied-stage">
      <div
        className={`kp-access-denied${compact ? ' kp-access-denied--compact' : ''}`}
        data-tone={safeTone}
      >
        <div className="kp-ad-icon">{ICONS[safeTone]}</div>

        <h2 className="kp-ad-title">{finalTitle}</h2>
        <p className="kp-ad-desc">{finalDesc}</p>

        {hasInfo && (
          <div className="kp-ad-info">
            {moduleName && (
              <div className="kp-ad-info-row">
                <span className="kp-ad-info-label">Modul</span>
                <span className="kp-ad-info-value">
                  <ModuleIcon />
                  {moduleName}
                </span>
              </div>
            )}
            {moduleName && requiredRole && <div className="kp-ad-info-divider" />}
            {requiredRole && (
              <div className="kp-ad-info-row">
                <span className="kp-ad-info-label">Krävs roll</span>
                <span className="kp-ad-role-badge">
                  <RoleIcon />
                  {requiredRole}
                </span>
              </div>
            )}
          </div>
        )}

        <div className="kp-ad-actions">
          {onRequestAccess && (
            <button
              type="button"
              className="kp-ad-btn kp-ad-btn--primary"
              onClick={handleRequestClick}
              disabled={requestState !== 'idle'}
              aria-label={moduleName ? `Begär åtkomst till ${moduleName}` : 'Begär åtkomst'}
              data-state={requestState}
            >
              {requestState === 'submitting' ? <SpinnerIcon /> : <CheckIcon />}
              {requestLabel}
            </button>
          )}
          {renderBack && (
            onBack ? (
              <button type="button" className="kp-ad-btn kp-ad-btn--secondary" onClick={onBack}>
                <ArrowLeftIcon />
                Tillbaka till startsidan
              </button>
            ) : (
              <Link to="/" className="kp-ad-btn kp-ad-btn--secondary">
                <ArrowLeftIcon />
                Tillbaka till startsidan
              </Link>
            )
          )}
        </div>

        {adminContactHref && !compact && (
          <p className="kp-ad-hint">
            Eller <a href={adminContactHref}>kontakta systemadministratör</a>
          </p>
        )}
      </div>
    </div>
  );
}
